import { isEqual } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Switcher } from 'components/atoms/switcher';
import { Filter } from 'components/molecules/filters/types';
import { TaskCompletionType } from 'components/organisms/tasks/views';
import { to_query } from 'components/pages/filter-transformer';
import { isEmpty, ofNullable, valueOf } from 'core/maybe';
import { useTranslation } from 'hooks/use-translation';
import { byLabel } from 'utils/comparators/by-label';
import { AssigneeSelect } from '../criteria/assignee';
import { DateCriteria } from '../criteria/date';
import {
  CreatedByEditorComponent,
  TagEditorComponents,
} from '../criteria/editor-components';
import { MultiSelectEditor } from '../criteria/multi-select-editor';
import { ResponsibleCriteria } from '../criteria/responsible';
import { SingleSelectEditor } from '../criteria/single-select-editor';
import {
  useProjectIdFromUrl,
  useUserOptions,
} from '../criteria/use-user-options';
import {
  AssigneeField,
  DateField,
  FilterableField,
  MultiSelectField,
  SingleSelectField,
  SingleUserField,
} from '../fields';
import { FieldFilter } from '../filters';
import { SelectFilterOperationType } from '../filters/select';
import { FilterView } from './components';
import { SimpleFilter } from './simple-filter';

export interface Props {
  lookup: Record<string, FilterableField>;
  filter: Filter;
  onChange: (filter: Filter) => void;
}

export const SimpleFilterComponent: FC<Props> = ({
  lookup,
  filter,
  onChange,
}) => {
  const projectId = useProjectIdFromUrl();
  const taskCompletionCaption = useTranslation(
    'all_tasks.task_completion.caption',
  );
  const [simpleFilter, setSimpleFilter] = useState(
    SimpleFilter.parse(filter, lookup),
  );
  const assigneeField = lookup.assignee as AssigneeField;
  const responsibleField = lookup.responsible as MultiSelectField;
  const projectField = lookup.project as SingleSelectField;
  const dueDateField = lookup.dueDate as DateField;
  const createdByField = lookup.createdBy as SingleUserField;
  const showTaskField = {
    fieldId: -1,
    fieldName: taskCompletionCaption,
    fieldLabel: taskCompletionCaption,
    private: false,
  } as FilterableField;

  const userOptions = useUserOptions(createdByField.metaData.users);
  const filterRef = useRef(filter);
  const onChangeRef = useRef(onChange);
  const lookupRef = useRef(lookup);

  useEffect(() => {
    filterRef.current = filter;
  }, [filterRef, filter]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChangeRef, onChange]);

  useEffect(() => {
    lookupRef.current = lookup;
  }, [lookupRef, lookup]);

  const simpleFilterRef = useRef(simpleFilter);

  useEffect(() => {
    simpleFilterRef.current = simpleFilter;
  }, [simpleFilterRef, simpleFilter]);

  useEffect(() => {
    if (
      !isEqual(
        {
          type: filter.type,
          criteria: filter.criteria.filter(FieldFilter.isValid),
        },
        SimpleFilter.toFilter(lookupRef.current)(simpleFilterRef.current),
      )
    ) {
      setSimpleFilter(SimpleFilter.parse(filter, lookup));
    }
  }, [filter, setSimpleFilter, simpleFilterRef, lookupRef, lookup]);

  useEffect(() => {
    const next = SimpleFilter.toFilter(lookupRef.current)(simpleFilter);

    if (to_query(filterRef.current) !== to_query(next)) {
      onChangeRef.current(next);
    }
  }, [simpleFilter, lookupRef, onChangeRef, filterRef]);

  return (
    <>
      <FilterView field={assigneeField}>
        <AssigneeSelect
          filterType={SelectFilterOperationType.IsOneOf}
          field={assigneeField}
          value={simpleFilter.assignee}
          onChange={(assignee) =>
            setSimpleFilter({ ...simpleFilter, assignee })
          }
        />
      </FilterView>
      <FilterView field={responsibleField}>
        <ResponsibleCriteria
          filter={simpleFilter.responsible as any}
          onChange={(responsible) =>
            setSimpleFilter({ ...simpleFilter, responsible })
          }
        />
      </FilterView>
      {isEmpty(projectId) ? (
        <FilterView field={projectField}>
          <MultiSelectEditor
            components={TagEditorComponents}
            field={projectField}
            filterType={SelectFilterOperationType.IsOneOf}
            options={projectField.metaData.options.slice().sort(byLabel)}
            value={simpleFilter.project}
            onChange={(project: number[]) =>
              setSimpleFilter({ ...simpleFilter, project })
            }
            type="tag"
          />
        </FilterView>
      ) : null}
      <FilterView field={dueDateField}>
        <DateCriteria
          filter={simpleFilter.dueDate as any}
          onChange={(dueDate) => setSimpleFilter({ ...simpleFilter, dueDate })}
        />
      </FilterView>
      <FilterView field={createdByField}>
        <SingleSelectEditor
          components={CreatedByEditorComponent}
          field={createdByField}
          filterType={SelectFilterOperationType.Is}
          options={userOptions.slice().sort(byLabel)}
          value={valueOf(simpleFilter.createdBy) ?? null}
          onChange={(selected: number | null) =>
            setSimpleFilter({
              ...simpleFilter,
              createdBy: ofNullable(selected),
            })
          }
          type="user"
        />
      </FilterView>
      <FilterView field={showTaskField}>
        <Switcher
          value={simpleFilter.status}
          onChange={(e) =>
            setSimpleFilter({
              ...simpleFilter,
              status: e.target.value,
            })
          }
        >
          {[
            TaskCompletionType.Incomplete,
            TaskCompletionType.Complete,
            TaskCompletionType.All,
          ].map((key) => (
            <Switcher.Button
              key={key}
              value={key}
              data-cy={`simple_filter.task_completion.${key}`}
            >
              <FormattedMessage id={`all_tasks.task_completion.${key}`} />
            </Switcher.Button>
          ))}
        </Switcher>
      </FilterView>
    </>
  );
};
